<script setup lang="ts">
// Full-width line graph. The header never changes on hover: the hovered
// time and value appear in a floating tooltip, with a crosshair. Data is
// frozen while hovered so live refreshes never move the point under the
// pointer. Tick labels along the bottom.
import { computed, ref } from 'vue'
import { clock, dayLabel, tipPos } from '../util'

// from/to (ms) is the window the graph represents; points are placed by time,
// so data that only covers part of the window leaves the rest empty
const props = defineProps<{
    times: number[]
    values: number[]
    from: number
    to: number
    height?: number
    color?: string
    label: string
    format?: (v: number) => string
    max?: number
    loading?: boolean // its samples are still coming: a placeholder over the empty graph
}>()
const h = computed(() => props.height ?? 56)
const w = 600
const frozen = ref<{ times: number[]; values: number[] } | null>(null)
const times = computed(() => frozen.value?.times ?? props.times)
const values = computed(() => frozen.value?.values ?? props.values)
// a fixed scale (100 for percentages) when given, stretched only by a value above it
const max = computed(() => Math.max(...values.value, props.max ?? 1))
const span = computed(() => Math.max(1, props.to - props.from))
const xOf = (tSec: number) => ((tSec * 1000 - props.from) / span.value) * w
const pts = computed(() => {
    const v = values.value,
        t = times.value
    if (v.length < 2) return []
    return v
        .map((y, i) => [xOf(t[i]!), h.value - (y / max.value) * (h.value - 4) - 2] as [number, number])
        .filter((p) => p[0] >= -1 && p[0] <= w + 1)
})
// break the line where samples are more than 3 intervals apart (gaps in data)
const path = computed(() => {
    const t = times.value
    if (pts.value.length < 2) return ''
    const gap = Math.max(1, t[1]! - t[0]!) * 3
    return pts.value
        .map((p, i) => `${i === 0 || t[i]! - t[i - 1]! > gap ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`)
        .join(' ')
})
const area = computed(() =>
    pts.value.length
        ? `${path.value} L${pts.value.at(-1)![0].toFixed(1)},${h.value} L${pts.value[0]![0].toFixed(1)},${h.value} Z`
        : '',
)
const hover = ref<{ i: number; x: number; y: number } | null>(null)
const svg = ref<SVGSVGElement | null>(null)
function enter() {
    frozen.value = { times: [...props.times], values: [...props.values] }
}
function leave() {
    hover.value = null
    frozen.value = null
}
function move(e: MouseEvent) {
    const r = svg.value!.getBoundingClientRect()
    const tMs = props.from + ((e.clientX - r.left) / r.width) * span.value
    // nearest sample in time
    let best = 0,
        bd = Infinity
    times.value.forEach((t, i) => {
        const d = Math.abs(t * 1000 - tMs)
        if (d < bd) {
            bd = d
            best = i
        }
    })
    hover.value = { i: best, ...tipPos(e, 260, 60) }
}
const fmt = (v: number) => (props.format ? props.format(v) : String(Math.round(v)))
const last = computed(() => (props.values.length ? fmt(props.values.at(-1)!) : '—'))
const timeOf = (i: number) => (times.value[i] ? new Date(times.value[i] * 1000) : null)
// tick labels across the window: five, so they never touch in a 400 px panel
const ticks = computed(() => {
    const n = 4
    const out: { x: number; label: string }[] = []
    for (let k = 0; k <= n; k++) {
        const d = new Date(props.from + (k / n) * span.value)
        out.push({ x: (k / n) * 100, label: span.value > 2 * 86400e3 ? dayLabel(d) : clock(d, span.value < 600e3) })
    }
    return out
})
// gridlines at 25/50/75% of max
const grid = [0.25, 0.5, 0.75]
</script>

<template>
    <div class="mt-2.5">
        <div class="flex items-center gap-2 text-xs">
            <span class="min-w-0 flex-1 truncate text-fg-2">{{ label }}</span
            ><span class="min-w-21.5 text-right font-mono tabular-nums">{{ last }}</span>
        </div>
        <div
            v-if="loading && !times.length"
            class="relative mt-0.5 overflow-hidden rounded bg-bg-2 motion-reduce:animate-pulse-dot"
            :style="{ height: h + 'px' }"
            role="progressbar"
            aria-busy="true"
            :aria-label="`loading ${label}`"
        >
            <div
                class="absolute inset-y-0 w-[30%] animate-indet bg-linear-to-r from-transparent via-fg/15 to-transparent motion-reduce:hidden"
            />
        </div>
        <svg
            v-else
            ref="svg"
            class="mt-0.5 block w-full cursor-crosshair rounded bg-bg-2"
            :viewBox="`0 0 ${w} ${h}`"
            preserveAspectRatio="none"
            :style="{ height: h + 'px' }"
            @mouseenter="enter"
            @mousemove="move"
            @mouseleave="leave"
        >
            <line
                v-for="g in grid"
                :key="g"
                x1="0"
                :x2="w"
                :y1="h - g * (h - 4) - 2"
                :y2="h - g * (h - 4) - 2"
                stroke="var(--line)"
                stroke-width="1"
                vector-effect="non-scaling-stroke"
            />
            <path :d="area" :fill="color ?? 'var(--accent)'" opacity=".12" />
            <path
                :d="path"
                fill="none"
                :stroke="color ?? 'var(--accent)'"
                stroke-width="1.5"
                vector-effect="non-scaling-stroke"
            />
            <template v-if="hover && pts[hover.i]">
                <line
                    :x1="pts[hover.i]![0]"
                    :x2="pts[hover.i]![0]"
                    y1="0"
                    :y2="h"
                    stroke="var(--fg-2)"
                    stroke-width="1"
                    vector-effect="non-scaling-stroke"
                    stroke-dasharray="2 2"
                />
                <circle
                    :cx="pts[hover.i]![0]"
                    :cy="pts[hover.i]![1]"
                    r="3"
                    :fill="color ?? 'var(--accent)'"
                    vector-effect="non-scaling-stroke"
                />
            </template>
        </svg>
        <div class="relative h-3.25 text-[10px] text-fg-3">
            <span
                v-for="(t, i) in ticks"
                :key="i"
                class="absolute -translate-x-1/2 whitespace-nowrap first:translate-x-0 last:-translate-x-full"
                :style="{ left: t.x + '%' }"
                >{{ t.label }}</span
            >
        </div>
        <Teleport to="body">
            <div
                v-if="hover && timeOf(hover.i)"
                class="pointer-events-none fixed z-30 max-w-[360px] rounded-md border border-line-strong bg-bg-3 px-2 py-1.5 text-xs"
                :style="{ left: hover.x + 'px', top: hover.y + 'px' }"
            >
                <span class="font-mono tabular-nums">{{ fmt(values[hover.i]!) }}</span>
                <span class="text-fg-2"> · {{ clock(timeOf(hover.i)!, true) }}</span>
            </div>
        </Teleport>
    </div>
</template>
